import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserCheck, 
  UserX, 
  UserPlus, 
  Shield, 
  ShieldCheck, 
  ShieldAlert, 
  Search, 
  Check, 
  X, 
  Edit2, 
  Trash2, 
  AlertCircle, 
  Clock, 
  Calendar, 
  Mail, 
  User as UserIcon, 
  CheckCircle2, 
  Sparkles,
  RefreshCw,
  Lock
} from 'lucide-react';
import { UserProfile, UserStatus } from '../../types';
import { 
  subscribeToUsers, 
  adminCreateUser, 
  adminUpdateUser, 
  adminValidateUser, 
  adminBlockUser, 
  adminDeleteUser, 
  adminValidateAllPending 
} from '../../lib/firebase';
import { ANIME_AVATARS } from '../../data/initialCatalog';

export const UserManagementTab: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'active' | 'blocked' | 'admin'>('all');
  
  // Action notifications
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Modals state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // New User Form State
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    displayName: '',
    email: '',
    password: '',
    role: 'user' as 'user' | 'admin',
    status: 'active' as UserStatus,
    avatar: ANIME_AVATARS[0].url
  });

  // Edit User Form State
  const [editForm, setEditForm] = useState<{
    uid: string;
    username: string;
    displayName: string;
    email: string;
    password?: string;
    role: 'user' | 'admin';
    status: UserStatus;
    avatar: string;
  } | null>(null);

  // Subscribe to real-time users from Firestore
  useEffect(() => {
    const unsubscribe = subscribeToUsers((userList) => {
      setUsers(userList);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Show auto-dismiss feedback
  const showFeedback = (type: 'success' | 'error', message: string) => {
    setFeedback({ type, message });
    setTimeout(() => {
      setFeedback(null);
    }, 4000);
  };

  // Quick Validate User
  const handleValidate = async (user: UserProfile) => {
    setIsProcessing(true);
    try {
      await adminValidateUser(user.uid, 'Administrador');
      showFeedback('success', `L'usuari "${user.displayName || user.username}" s'ha validat i activat correctament.`);
    } catch (err: any) {
      showFeedback('error', `Error al validar usuari: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Quick Block User
  const handleBlock = async (user: UserProfile) => {
    if (!window.confirm(`Vols desactivar/bloquejar l'accés a "${user.displayName || user.username}"?`)) {
      return;
    }
    setIsProcessing(true);
    try {
      await adminBlockUser(user.uid);
      showFeedback('success', `L'usuari "${user.displayName || user.username}" ha estat bloquejat.`);
    } catch (err: any) {
      showFeedback('error', `Error al bloquejar usuari: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Quick Delete User
  const handleDeleteConfirm = async () => {
    if (!deletingUserId) return;
    setIsProcessing(true);
    try {
      await adminDeleteUser(deletingUserId);
      showFeedback('success', 'Usuari eliminat de la base de dades amb èxit.');
      setDeletingUserId(null);
    } catch (err: any) {
      showFeedback('error', `Error al eliminar l'usuari: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Validate all pending users
  const handleValidateAll = async () => {
    const pendingUsers = users.filter(u => u.status === 'pending');
    if (pendingUsers.length === 0) return;
    
    if (!window.confirm(`Vols validar i activar tots els ${pendingUsers.length} usuaris pendents d'un sol cop?`)) {
      return;
    }

    setIsProcessing(true);
    try {
      const count = await adminValidateAllPending('Administrador');
      showFeedback('success', `S'han validat i activat ${count} usuaris pendents!`);
    } catch (err: any) {
      showFeedback('error', `Error en la validació massiva: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Create User Submit
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserForm.username.trim()) {
      showFeedback('error', "El nom d'usuari és obligatori.");
      return;
    }

    setIsProcessing(true);
    try {
      await adminCreateUser({
        username: newUserForm.username.trim(),
        displayName: newUserForm.displayName.trim() || newUserForm.username.trim(),
        email: newUserForm.email.trim(),
        password: newUserForm.password.trim() || undefined,
        role: newUserForm.role,
        status: newUserForm.status,
        avatar: newUserForm.avatar
      });

      showFeedback('success', `Usuari "${newUserForm.username}" creat correctament!`);
      setIsCreateModalOpen(false);
      setNewUserForm({
        username: '',
        displayName: '',
        email: '',
        password: '',
        role: 'user',
        status: 'active',
        avatar: ANIME_AVATARS[0].url
      });
    } catch (err: any) {
      showFeedback('error', `Error creant l'usuari: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Edit User Submit
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm) return;

    setIsProcessing(true);
    try {
      await adminUpdateUser(editForm.uid, {
        username: editForm.username.trim(),
        displayName: editForm.displayName.trim() || editForm.username.trim(),
        email: editForm.email.trim(),
        ...(editForm.password !== undefined && editForm.password !== '' ? { password: editForm.password } : {}),
        role: editForm.role,
        status: editForm.status,
        avatar: editForm.avatar,
        validatedAt: editForm.status === 'active' ? (editForm.status === 'active' ? new Date().toISOString() : undefined) : undefined
      });

      showFeedback('success', `Dades de l'usuari "${editForm.displayName || editForm.username}" actualitzades!`);
      setEditingUser(null);
      setEditForm(null);
    } catch (err: any) {
      showFeedback('error', `Error modificant l'usuari: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Open edit modal helper
  const openEditModal = (user: UserProfile) => {
    setEditingUser(user);
    setEditForm({
      uid: user.uid,
      username: user.username || '',
      displayName: user.displayName || user.username || '',
      email: user.email || '',
      password: user.password || '',
      role: user.role || 'user',
      status: user.status || 'active',
      avatar: user.avatar || ANIME_AVATARS[0].url
    });
  };

  // Counters
  const pendingCount = users.filter(u => u.status === 'pending').length;
  const activeCount = users.filter(u => u.status === 'active').length;
  const blockedCount = users.filter(u => u.status === 'blocked').length;
  const adminCount = users.filter(u => u.role === 'admin').length;

  // Filtered Users List
  const filteredUsers = users.filter(user => {
    // Status Filter
    if (statusFilter === 'pending' && user.status !== 'pending') return false;
    if (statusFilter === 'active' && user.status !== 'active') return false;
    if (statusFilter === 'blocked' && user.status !== 'blocked') return false;
    if (statusFilter === 'admin' && user.role !== 'admin') return false;

    // Search Query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const matchName = user.displayName?.toLowerCase().includes(q);
      const matchUsername = user.username?.toLowerCase().includes(q);
      const matchEmail = user.email?.toLowerCase().includes(q);
      return matchName || matchUsername || matchEmail;
    }

    return true;
  });

  return (
    <div className="space-y-6 max-w-5xl">
      
      {/* Top Header & Overview */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-display font-black text-xl text-white flex items-center gap-2.5">
            <Users className="w-6 h-6 text-amber-400" />
            Gestió i Validació d'Usuaris
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Administra els comptes d'usuari registrats, valida els nous accessos i gestiona rols i permisos.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {pendingCount > 0 && (
            <button
              id="validate-all-pending-btn"
              onClick={handleValidateAll}
              disabled={isProcessing}
              className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-bold text-xs shadow-lg shadow-emerald-500/20 flex items-center gap-1.5 transition-all"
            >
              <UserCheck className="w-4 h-4" />
              <span>Validar Tots els Pendents ({pendingCount})</span>
            </button>
          )}

          <button
            id="create-user-modal-btn"
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs shadow-md shadow-amber-500/20 flex items-center gap-1.5 transition-all"
          >
            <UserPlus className="w-4 h-4" />
            <span>Afegir Nou Usuari</span>
          </button>
        </div>
      </div>

      {/* Feedback Banner */}
      {feedback && (
        <div className={`p-3.5 rounded-2xl border text-xs flex items-center gap-2.5 animate-in fade-in slide-in-from-top-2 ${
          feedback.type === 'success' 
            ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' 
            : 'bg-red-500/15 border-red-500/30 text-red-300'
        }`}>
          {feedback.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* Stats Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        
        {/* Total Users */}
        <div 
          onClick={() => setStatusFilter('all')}
          className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${
            statusFilter === 'all' 
              ? 'bg-[#182032] border-amber-500/50 shadow-md shadow-amber-500/10' 
              : 'bg-[#10141f] border-white/5 hover:border-white/10'
          }`}
        >
          <div className="flex items-center justify-between text-slate-400 mb-1">
            <span className="text-[11px] font-semibold">Total Usuaris</span>
            <Users className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-xl font-black text-white">{users.length}</div>
        </div>

        {/* Pending Validation */}
        <div 
          onClick={() => setStatusFilter('pending')}
          className={`p-3.5 rounded-2xl border transition-all cursor-pointer relative overflow-hidden ${
            statusFilter === 'pending'
              ? 'bg-amber-500/15 border-amber-500 shadow-md shadow-amber-500/20'
              : pendingCount > 0 
                ? 'bg-amber-500/10 border-amber-500/30 hover:border-amber-500/60 animate-pulse' 
                : 'bg-[#10141f] border-white/5 hover:border-white/10'
          }`}
        >
          <div className="flex items-center justify-between text-amber-300 mb-1">
            <span className="text-[11px] font-semibold">Pendents</span>
            <Clock className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-black text-amber-400">{pendingCount}</span>
            {pendingCount > 0 && (
              <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300">
                Requereix Validació
              </span>
            )}
          </div>
        </div>

        {/* Active Users */}
        <div 
          onClick={() => setStatusFilter('active')}
          className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${
            statusFilter === 'active' 
              ? 'bg-emerald-500/15 border-emerald-500 shadow-md' 
              : 'bg-[#10141f] border-white/5 hover:border-white/10'
          }`}
        >
          <div className="flex items-center justify-between text-emerald-400 mb-1">
            <span className="text-[11px] font-semibold">Actius / Validats</span>
            <UserCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-xl font-black text-emerald-300">{activeCount}</div>
        </div>

        {/* Blocked Users */}
        <div 
          onClick={() => setStatusFilter('blocked')}
          className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${
            statusFilter === 'blocked' 
              ? 'bg-red-500/15 border-red-500 shadow-md' 
              : 'bg-[#10141f] border-white/5 hover:border-white/10'
          }`}
        >
          <div className="flex items-center justify-between text-red-400 mb-1">
            <span className="text-[11px] font-semibold">Bloquejats</span>
            <UserX className="w-4 h-4 text-red-400" />
          </div>
          <div className="text-xl font-black text-red-300">{blockedCount}</div>
        </div>

        {/* Admins */}
        <div 
          onClick={() => setStatusFilter('admin')}
          className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${
            statusFilter === 'admin' 
              ? 'bg-purple-500/15 border-purple-500 shadow-md' 
              : 'bg-[#10141f] border-white/5 hover:border-white/10'
          }`}
        >
          <div className="flex items-center justify-between text-purple-400 mb-1">
            <span className="text-[11px] font-semibold">Admins</span>
            <ShieldCheck className="w-4 h-4 text-purple-400" />
          </div>
          <div className="text-xl font-black text-purple-300">{adminCount}</div>
        </div>

      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-[#10141f] p-3 rounded-2xl border border-white/5">
        
        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 w-full sm:w-auto overflow-x-auto no-scrollbar pb-1 sm:pb-0">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              statusFilter === 'all' 
                ? 'bg-amber-500 text-black' 
                : 'bg-white/5 text-slate-300 hover:bg-white/10'
            }`}
          >
            Tots ({users.length})
          </button>
          
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors flex items-center gap-1 ${
              statusFilter === 'pending' 
                ? 'bg-amber-500 text-black' 
                : 'bg-white/5 text-amber-400 hover:bg-white/10'
            }`}
          >
            <span>Pendents ({pendingCount})</span>
            {pendingCount > 0 && <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />}
          </button>

          <button
            onClick={() => setStatusFilter('active')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              statusFilter === 'active' 
                ? 'bg-emerald-500 text-slate-950 font-bold' 
                : 'bg-white/5 text-emerald-400 hover:bg-white/10'
            }`}
          >
            Actius ({activeCount})
          </button>

          <button
            onClick={() => setStatusFilter('blocked')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              statusFilter === 'blocked' 
                ? 'bg-red-500 text-white' 
                : 'bg-white/5 text-red-400 hover:bg-white/10'
            }`}
          >
            Bloquejats ({blockedCount})
          </button>

          <button
            onClick={() => setStatusFilter('admin')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              statusFilter === 'admin' 
                ? 'bg-purple-500 text-white' 
                : 'bg-white/5 text-purple-300 hover:bg-white/10'
            }`}
          >
            Admins ({adminCount})
          </button>
        </div>

        {/* Search Field */}
        <div className="relative w-full sm:w-64">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            id="user-search-input"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca per nom, usuari o email..."
            className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-amber-500 focus:outline-none placeholder-slate-500"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

      </div>

      {/* Users List / Table Cards */}
      <div className="space-y-3">
        {loading ? (
          <div className="p-12 text-center text-slate-400 flex flex-col items-center gap-2">
            <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
            <p className="text-xs">Carregant usuaris de Firestore...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center bg-[#10141f] rounded-2xl border border-white/5 space-y-2">
            <Users className="w-8 h-8 text-slate-500 mx-auto" />
            <h4 className="text-sm font-bold text-white">No s'ha trobat cap usuari</h4>
            <p className="text-xs text-slate-400">
              {searchQuery ? "Cap usuari coincideix amb la cerca." : "No hi ha cap usuari amb aquest filtre."}
            </p>
          </div>
        ) : (
          filteredUsers.map((user) => {
            const isPending = user.status === 'pending';
            const isBlocked = user.status === 'blocked';
            const isActive = user.status === 'active';
            const isAdmin = user.role === 'admin';

            return (
              <div
                key={user.uid}
                className={`p-4 rounded-2xl border transition-all flex flex-col md:flex-row items-start md:items-center justify-between gap-4 ${
                  isPending 
                    ? 'bg-[#181a24] border-amber-500/40 shadow-lg shadow-amber-500/5' 
                    : isBlocked
                    ? 'bg-[#1a1215] border-red-500/30'
                    : 'bg-[#10141f] border-white/5 hover:border-white/15'
                }`}
              >
                {/* User Info with Avatar */}
                <div className="flex items-center gap-3.5 min-w-0">
                  
                  {/* Avatar */}
                  <div className="relative shrink-0">
                    <img
                      src={user.avatar || ANIME_AVATARS[0].url}
                      alt={user.displayName || user.username}
                      className="w-12 h-12 rounded-2xl object-cover border-2 border-white/10 bg-slate-900"
                      referrerPolicy="no-referrer"
                    />
                    {isAdmin && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-md" title="Administrador">
                        <Shield className="w-2.5 h-2.5" />
                      </div>
                    )}
                  </div>

                  {/* Text Details */}
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-white text-sm truncate">
                        {user.displayName || user.username}
                      </span>
                      <span className="font-mono text-xs text-amber-400">
                        @{user.username}
                      </span>

                      {/* Role Badge */}
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                        isAdmin 
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                          : 'bg-white/5 text-slate-400 border border-white/5'
                      }`}>
                        {isAdmin ? 'Admin' : 'Usuari'}
                      </span>

                      {/* Status Badge */}
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold flex items-center gap-1 ${
                        isPending
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 animate-pulse'
                          : isBlocked
                          ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                          : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      }`}>
                        {isPending ? (
                          <>
                            <Clock className="w-2.5 h-2.5" />
                            <span>Pendent de Validació</span>
                          </>
                        ) : isBlocked ? (
                          <>
                            <UserX className="w-2.5 h-2.5" />
                            <span>Bloquejat</span>
                          </>
                        ) : (
                          <>
                            <Check className="w-2.5 h-2.5" />
                            <span>Actiu (Validat)</span>
                          </>
                        )}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-[11px] text-slate-400 flex-wrap">
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3 text-slate-500 shrink-0" />
                        <span className="truncate">{user.email || `${user.username}@animecat.cat`}</span>
                      </span>

                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-slate-500" />
                        <span>Registrat: {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ca-ES') : 'Recent'}</span>
                      </span>

                      {user.validatedAt && (
                        <span className="text-emerald-400/80">
                          Validat el {new Date(user.validatedAt).toLocaleDateString('ca-ES')}
                        </span>
                      )}
                    </div>
                  </div>

                </div>

                {/* Interactive Actions for this user */}
                <div className="flex items-center gap-2 self-end md:self-center shrink-0 w-full md:w-auto justify-end border-t md:border-t-0 pt-2 md:pt-0 border-white/5">
                  
                  {/* Validate / Activate Button */}
                  {(isPending || isBlocked) && (
                    <button
                      id={`validate-user-${user.uid}`}
                      onClick={() => handleValidate(user)}
                      disabled={isProcessing}
                      className="px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 shadow-md shadow-emerald-500/20 transition-colors"
                      title="Aprova i activa l'usuari"
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                      <span>Validar i Activar</span>
                    </button>
                  )}

                  {/* Block / Deactivate Button */}
                  {isActive && !isAdmin && (
                    <button
                      id={`block-user-${user.uid}`}
                      onClick={() => handleBlock(user)}
                      disabled={isProcessing}
                      className="px-2.5 py-1.5 rounded-xl bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-300 border border-white/10 hover:border-red-500/30 text-xs font-semibold flex items-center gap-1 transition-colors"
                      title="Desactivar o bloquejar l'usuari"
                    >
                      <UserX className="w-3.5 h-3.5 text-red-400" />
                      <span className="hidden sm:inline">Bloquejar</span>
                    </button>
                  )}

                  {/* Edit User Button */}
                  <button
                    id={`edit-user-${user.uid}`}
                    onClick={() => openEditModal(user)}
                    className="p-2 rounded-xl bg-white/5 hover:bg-amber-500 hover:text-black text-slate-300 border border-white/10 transition-colors"
                    title="Editar usuari"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>

                  {/* Delete User Button */}
                  <button
                    id={`delete-user-${user.uid}`}
                    onClick={() => setDeletingUserId(user.uid)}
                    className="p-2 rounded-xl bg-white/5 hover:bg-red-500 hover:text-white text-slate-400 border border-white/10 transition-colors"
                    title="Eliminar de Firestore"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>

                </div>

              </div>
            );
          })
        )}
      </div>

      {/* ======================================================== */}
      {/* MODAL: CREATE USER                                       */}
      {/* ======================================================== */}
      {isCreateModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in"
          onClick={() => setIsCreateModalOpen(false)}
        >
          <div 
            className="relative w-full max-w-lg bg-[#0e121c] border border-white/10 rounded-3xl p-6 sm:p-7 shadow-2xl space-y-4 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-amber-500/20 text-amber-400">
                  <UserPlus className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-white">
                    Crear Nou Usuari
                  </h3>
                  <p className="text-xs text-slate-400">
                    Afegeix un usuari directament des de la consola d'administració.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Nom d'Usuari (Identificador de sessió) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: conan_edogawa o vegeta"
                  value={newUserForm.username}
                  onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Nom Visible (Display Name)
                </label>
                <input
                  type="text"
                  placeholder="Ex: Detectiu Conan"
                  value={newUserForm.displayName}
                  onChange={(e) => setNewUserForm({ ...newUserForm, displayName: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Correu Electrònic (Opcional)
                </label>
                <input
                  type="email"
                  placeholder="Deixa buit per auto-generar @animecat.cat"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-sky-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Contrasenya (Opcional - per defecte: 123456)
                </label>
                <input
                  type="text"
                  placeholder="Escriu la contrasenya (modificable a Firestore)"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-sky-500 focus:outline-none font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Rol d'Accés
                  </label>
                  <select
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value as any })}
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-amber-500 focus:outline-none"
                  >
                    <option value="user">Usuari Estàndard</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Estat Inicial
                  </label>
                  <select
                    value={newUserForm.status}
                    onChange={(e) => setNewUserForm({ ...newUserForm, status: e.target.value as any })}
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-amber-500 focus:outline-none"
                  >
                    <option value="active">Actiu (Validat)</option>
                    <option value="pending">Pendent de Validació</option>
                    <option value="blocked">Bloquejat</option>
                  </select>
                </div>
              </div>

              {/* Avatar Selector */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-2">
                  Tria Avatar d'Anime
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {ANIME_AVATARS.map((av) => (
                    <button
                      key={av.id}
                      type="button"
                      onClick={() => setNewUserForm({ ...newUserForm, avatar: av.url })}
                      className={`relative rounded-xl overflow-hidden aspect-square border-2 transition-all ${
                        newUserForm.avatar === av.url 
                          ? 'border-amber-400 scale-105 shadow-md shadow-amber-500/30' 
                          : 'border-white/10 opacity-70 hover:opacity-100'
                      }`}
                      title={av.name}
                    >
                      <img
                        src={av.url}
                        alt={av.name}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-white/10 text-white text-xs font-semibold"
                >
                  Cancel·lar
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black text-xs font-bold shadow-md disabled:opacity-50"
                >
                  {isProcessing ? 'Creant...' : 'Crear Usuari'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: EDIT USER                                         */}
      {/* ======================================================== */}
      {editingUser && editForm && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in"
          onClick={() => {
            setEditingUser(null);
            setEditForm(null);
          }}
        >
          <div 
            className="relative w-full max-w-lg bg-[#0e121c] border border-white/10 rounded-3xl p-6 sm:p-7 shadow-2xl space-y-4 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-amber-500/20 text-amber-400">
                  <Edit2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-white">
                    Editar Usuari: {editingUser.displayName || editingUser.username}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Modifica el nom, rol o estat de validació de l'usuari.
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setEditingUser(null);
                  setEditForm(null);
                }}
                className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleEditSubmit} className="space-y-4">
              
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Nom Visible
                </label>
                <input
                  type="text"
                  required
                  value={editForm.displayName}
                  onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Nom d'Usuari
                </label>
                <input
                  type="text"
                  required
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Correu Electrònic
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-sky-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Contrasenya (guardada a Firestore per a login directe)
                </label>
                <input
                  type="text"
                  placeholder="Deixa buit per mantenir la contrasenya actual"
                  value={editForm.password || ''}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-sky-500 focus:outline-none font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  L'usuari també pot modificar directament aquest camp al document d'usuari a Firestore.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Rol
                  </label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as any })}
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-amber-500 focus:outline-none"
                  >
                    <option value="user">Usuari Estàndard</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Estat de Validació
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value as any })}
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-amber-500 focus:outline-none"
                  >
                    <option value="active">Actiu (Validat)</option>
                    <option value="pending">Pendent de Validació</option>
                    <option value="blocked">Bloquejat / Desactivat</option>
                  </select>
                </div>
              </div>

              {/* Avatar Selector */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-2">
                  Avatar d'Anime
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {ANIME_AVATARS.map((av) => (
                    <button
                      key={av.id}
                      type="button"
                      onClick={() => setEditForm({ ...editForm, avatar: av.url })}
                      className={`relative rounded-xl overflow-hidden aspect-square border-2 transition-all ${
                        editForm.avatar === av.url 
                          ? 'border-amber-400 scale-105 shadow-md shadow-amber-500/30' 
                          : 'border-white/10 opacity-70 hover:opacity-100'
                      }`}
                      title={av.name}
                    >
                      <img
                        src={av.url}
                        alt={av.name}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    setEditingUser(null);
                    setEditForm(null);
                  }}
                  className="px-4 py-2 rounded-xl bg-white/10 text-white text-xs font-semibold"
                >
                  Cancel·lar
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="px-6 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold shadow-md disabled:opacity-50"
                >
                  {isProcessing ? 'Desant...' : 'Desar Canvis'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: CONFIRM DELETE USER                               */}
      {/* ======================================================== */}
      {deletingUserId && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in"
          onClick={() => setDeletingUserId(null)}
        >
          <div 
            className="relative w-full max-w-sm bg-[#0e121c] border border-red-500/30 rounded-3xl p-6 shadow-2xl space-y-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-2xl bg-red-500/20 text-red-400 flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="space-y-1">
              <h3 className="font-display font-bold text-lg text-white">
                Eliminar Usuari?
              </h3>
              <p className="text-xs text-slate-400">
                Aquesta acció esborrarà permanentment el registre d'usuari de Firestore. Aquesta acció no es pot desfer.
              </p>
            </div>

            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeletingUserId(null)}
                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold"
              >
                Cancel·lar
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={isProcessing}
                className="px-5 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold shadow-md"
              >
                {isProcessing ? 'Eliminant...' : 'Sí, Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
